import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useUpdateRegistration } from '../../../../screens/registration/useRegistrationForm/useUpdateRegistration';
import { ApiRequestError, isSolarSubmitErrorCode, submitRegistration, toApiRequestError } from '../../../../services/api';
import useStore from '../../../../hooks/useStore';
import { useRegistrationSteps } from '../../hooks/useRegistrationSteps';
import { DetailingStep } from '../../../../components/registering/utils/registerEnums';
import { Solcelledetaljer, SolcelleSone } from '../../../../types/registration/userInput';
import { updateRegistration as updateRegistrationAction } from '../../../../store/registration/actions';

import { SolcellerFormData } from './types';

const isDefined = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;

type KwpPanel = NonNullable<SolcellerFormData['kwpPanels']>[number];
type SqmPanel = NonNullable<SolcellerFormData['sqmPanels']>[number];
type CompleteKwpPanel = KwpPanel & { effect: number; tilt: number; azimuth: number };
type CompleteSqmPanel = SqmPanel & { panelType: 'solcellepanel' | 'solcelletakstein'; tilt: number; azimuth: number };

const isValidKwpPanel = (panel: KwpPanel): panel is CompleteKwpPanel =>
  isDefined(panel.effect) && isDefined(panel.tilt) && isDefined(panel.azimuth);

const isValidSqmPanel = (panel: SqmPanel): panel is CompleteSqmPanel => {
  if (!isDefined(panel.panelType) || !isDefined(panel.tilt) || !isDefined(panel.azimuth)) {
    return false;
  }

  if (panel.panelType === 'solcellepanel') {
    return isDefined(panel.area);
  }

  if (panel.hasActivePanelArea === 'yes') {
    return isDefined(panel.area);
  }

  if (panel.hasActivePanelArea === 'no') {
    return isDefined(panel.roofArea);
  }

  return false;
};

const formDataToSoner = (data: SolcellerFormData): SolcelleSone[] => {
  const soner: SolcelleSone[] = [];

  if (data.hasCapacityData === 'yes' && data.kwpPanels?.length) {
    for (const panel of data.kwpPanels) {
      if (isValidKwpPanel(panel)) {
        soner.push({
          azimut: panel.azimuth,
          helling: panel.tilt,
          effektKWp: panel.effect
        });
      }
    }
  } else if (data.hasCapacityData === 'no' && data.sqmPanels?.length) {
    for (const panel of data.sqmPanels) {
      if (isValidSqmPanel(panel)) {
        soner.push({
          azimut: panel.azimuth,
          helling: panel.tilt,
          panelType: panel.panelType,
          aktivtAreal:
            panel.panelType === 'solcellepanel' || panel.hasActivePanelArea === 'yes'
              ? panel.area
              : undefined,
          takAreal:
            panel.panelType === 'solcelletakstein' && panel.hasActivePanelArea === 'no'
              ? panel.roofArea
              : undefined,
        });
      }
    }
  }

  return soner;
};

const formDataToFordelingsnokkel = (data: SolcellerFormData): number | undefined => {
  if (data.sharedSolarInstallation !== 'yes') {
    return undefined;
  }

  if (data.sharedSolarDistribution === 'equal' && data.totalSharedBoenheter) {
    return 1 / data.totalSharedBoenheter;
  }

  if (data.sharedSolarDistribution === 'percentage' && data.fordelingsnokkelPercent) {
    return data.fordelingsnokkelPercent / 100;
  }

  return undefined;
};

const formDataToProduksjonsdataGjelder = (
  data: SolcellerFormData
): Solcelledetaljer['produksjonsdataGjelder'] => {
  if (
    data.sharedSolarInstallation !== 'yes' ||
    data.productionData === 'unknown' ||
    !data.productionDataScope
  ) {
    return undefined;
  }

  return data.productionDataScope === 'thisBoenhet' ? 'denneBoenheten' : 'heleAnlegget';
};

export const useSubmitSolceller = () => {
  const { registrationId } = useParams();
  const { updateRegistrationAsync, updateIsLoading } = useUpdateRegistration(registrationId);
  const dispatch = useDispatch();
  const store = useStore();
  const userInput = store.getState().register.userInput;
  const { nextStep } = useRegistrationSteps(DetailingStep.Solceller);
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<ApiRequestError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitSolceller = async (
    data: SolcellerFormData,
    onSuccess?: () => void,
    shouldCalculate: boolean = true
  ) => {
    setSubmitError(null);
    setIsSubmitting(true);
    const soner = formDataToSoner(data);

    const solcelledetaljer: Solcelledetaljer = {
      installasjonsAr: data.installationYear,
      produsertKwh: data.productionData === 'actualProduction' ? data.producedKwh : undefined,
      beregnetKwh: data.productionData === 'calculatedProduction' ? data.calculatedKwh : undefined,
      soner,
      latitude: data.latitude,
      longitude: data.longitude,
      deltAnlegg: data.sharedSolarInstallation === 'yes',
      fordelingsnokkel: formDataToFordelingsnokkel(data),
      produksjonsdataGjelder: formDataToProduksjonsdataGjelder(data),
      totaltAntallBoenheterSomDeler:
        data.sharedSolarInstallation === 'yes' && data.sharedSolarDistribution === 'equal'
          ? data.totalSharedBoenheter
          : undefined
    };

    const updatedUserInput = { ...userInput, solcelledetaljer };

    try {
      const updateResponse = await updateRegistrationAsync({
        registrationId,
        userInput: updatedUserInput
      });

      if (shouldCalculate) {
        const submitResponse = await submitRegistration(registrationId, updateResponse?.userInput ?? updatedUserInput);
        dispatch(updateRegistrationAction({ userInput: submitResponse?.userInput ?? updateResponse?.userInput ?? updatedUserInput }));
      } else {
        dispatch(updateRegistrationAction({ userInput: updateResponse?.userInput ?? updatedUserInput }));
      }

      (onSuccess ?? (() => navigate(`../${nextStep}`)))();
    } catch (error) {
      if (error instanceof ApiRequestError && isSolarSubmitErrorCode(error.errorCode)) {
        setSubmitError(error);
        return;
      }

      setSubmitError(
        toApiRequestError(error, 'Could not calculate energy rating after updating solceller')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitSolceller, isLoading: updateIsLoading || isSubmitting, submitError };
};
